"use client"

import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import {
  ArrowLeft, Plus, Trash2, Copy, Edit3, GripVertical, MapPin, Users,
  Clock, Bus, Loader2, ClipboardPaste, Flag, Locate,
} from "lucide-react"
import StudentAssignmentModal from "./StudentAssignmentModal"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useVehicleRoute } from "./Vehicle/hooks/useVehicleRoute"
import { useRouteMap } from "./Vehicle/hooks/useRouteMap"
import { useToast } from "../../hooks/use-toast"

export default function EditVehicle() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const {
    vehicleRoute, vehicle,
    selectedShift, setSelectedShift,
    editingShift, setEditingShift,
    loading, saving,
    availableStudents,
    studentModalOpen, setStudentModalOpen,
    selectedStop,
    shiftTypeModalOpen, setShiftTypeModalOpen,
    newShiftType, setNewShiftType,
    deleteShiftModalOpen, setDeleteShiftModalOpen,
    shiftToDelete,
    copyShiftModalOpen, setCopyShiftModalOpen,
    shiftToCopy,
    originalShiftName, setOriginalShiftName,
    currentLocationStopId,
    addingStopShiftId,
    deletingStopId,
    deletingShiftId,
    currentShift,
    saveVehicleRoute,
    handleAddShift,
    handleCreateShift,
    handleCopyShiftClick,
    handleCopyShiftConfirmed,
    handleShiftTypeChange,
    handleDeleteShiftConfirm,
    handleDeleteShiftConfirmed,
    onDragEnd,
    handleAddStop,
    handleDeleteStop,
    handleCopyStop,
    handleEditStop,
    handlePasteCoordinates,
    handleSetCurrentLocation,
    handleEditShiftName,
    handleShiftNameBlur,
    handleOpenStudentModal,
    handleStudentsUpdated,
  } = useVehicleRoute()

  useRouteMap(currentShift)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm sm:text-base">Loading vehicle route...</span>
        </div>
      </div>
    )
  }

  if (!vehicleRoute || !vehicle) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Vehicle not found</h2>
          <p className="text-gray-600 mb-4">The vehicle you're looking for doesn't exist.</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Button variant="outline" size="sm" className="gap-2 bg-transparent w-fit" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 shrink-0" />
              <span className="sm:hidden">Back</span>
              <span className="hidden sm:inline">Back to Vehicles</span>
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#0263cc]/10 rounded-lg shrink-0">
                <Bus className="h-5 w-5 text-[#0263cc]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{vehicle.vehicleNumber}</h1>
                <p className="text-xs sm:text-sm text-gray-500">Vehicle Management</p>
              </div>
            </div>
          </div>
          <Button
            onClick={saveVehicleRoute}
            disabled={saving}
            className="gap-2 bg-[#0263cc] hover:bg-[#0263cc]/90 w-full sm:w-auto shrink-0"
            title="Save changes and rebuild route polyline"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span className="truncate">{saving ? "Saving..." : "Save Changes"}</span>
          </Button>
        </div>

        {/* Route Shifts */}
        <Card>
          <CardHeader className="pb-3 px-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base sm:text-lg">Route Shifts</CardTitle>
              <Button onClick={handleAddShift} size="sm" className="gap-2 bg-[#0263cc] hover:bg-[#0263cc]/90 w-full sm:w-auto">
                <Plus className="h-4 w-4" />
                Add Shift
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <TooltipProvider>
              <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
                {vehicleRoute.shifts.map((shift) => (
                  <div key={shift._id} className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 min-w-0">
                    <div className="flex flex-col gap-1.5 sm:gap-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button
                          variant={selectedShift === shift._id ? "default" : "outline"}
                          onClick={() => setSelectedShift(shift._id)}
                          className={`flex items-center gap-2 min-w-fit ${
                            selectedShift === shift._id
                              ? "bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <Clock className="h-4 w-4" />
                          {editingShift === shift._id ? (
                            <Input
                              value={shift.name}
                              onChange={(e) => handleEditShiftName(shift._id, e.target.value)}
                              onBlur={(e) => {
                                handleShiftNameBlur(shift._id, e.target.value)
                                setEditingShift(null)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleShiftNameBlur(shift._id, e.target.value)
                                  setEditingShift(null)
                                } else if (e.key === "Escape") {
                                  if (originalShiftName !== null) handleEditShiftName(shift._id, originalShiftName)
                                  setEditingShift(null)
                                  setOriginalShiftName(null)
                                }
                              }}
                              className="h-6 text-sm bg-white/20 border-white/30 text-white placeholder:text-white/70 focus:bg-white focus:text-gray-900 min-w-[150px]"
                              autoFocus
                            />
                          ) : (
                            <span
                              onDoubleClick={() => {
                                setEditingShift(shift._id)
                                setOriginalShiftName(shift.name)
                              }}
                              className="cursor-pointer"
                              title="Double-click to edit"
                            >
                              {shift.name}
                            </span>
                          )}
                        </Button>

                        {shift.stops && shift.stops.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCopyShiftClick(shift._id)}
                                className="h-8 w-8 p-0 text-[#0263cc] hover:text-[#0263cc]/80 hover:bg-[#0263cc]/10"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Copy to {shift.type === 'pickup' ? 'Drop' : 'Pickup'} shift with reversed stops</p>
                            </TooltipContent>
                          </Tooltip>
                        )}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteShiftConfirm(shift)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete shift"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-1">
                        <Select
                          value={shift.type}
                          onValueChange={(value) => handleShiftTypeChange(shift._id, value)}
                          disabled={!!vehicleRoute._id}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pickup">Pickup</SelectItem>
                            <SelectItem value="drop">Drop</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TooltipProvider>
          </CardContent>
        </Card>

        {/* Route Preview Map */}
        {currentShift && (
          <Card>
            <CardHeader className="px-4 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Route Preview</CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div
                id="route-preview-map"
                className="h-[260px] sm:h-[320px] md:h-[400px] w-full min-h-[200px] rounded-lg border border-gray-200 overflow-hidden bg-gray-100"
                style={{ position: "relative", zIndex: 1 }}
              />
              {currentShift.stops && currentShift.stops.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-4 justify-center md:justify-start p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#00bcd4] border-2 border-white shadow-sm" />
                    <span className="text-sm font-medium text-gray-700">Start</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#4629eb] border-2 border-white shadow-sm" />
                    <span className="text-sm font-medium text-gray-700">Stop</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#ff6b35] border-2 border-white shadow-sm" />
                    <span className="text-sm font-medium text-gray-700">End</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-8 bg-[#4629eb] opacity-70" />
                    <span className="text-sm font-medium text-gray-700">Route</span>
                  </div>
                </div>
              )}
              {(!currentShift.stops || currentShift.stops.length === 0) && (
                <div className="mt-4 text-center text-gray-500 text-sm">
                  Add stops to see route preview on map
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Route Stops */}
        {currentShift && (
          <Card>
            <CardHeader className="px-4 sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <CardTitle className="text-base sm:text-lg">Route Stops</CardTitle>
                  <Badge variant="outline" className="gap-1 w-fit">
                    <MapPin className="h-3 w-3" />
                    {currentShift.stops.length} stops
                  </Badge>
                </div>
                <Button
                  onClick={() => handleAddStop(currentShift._id)}
                  disabled={addingStopShiftId === currentShift._id}
                  className="gap-2 bg-[#0263cc] hover:bg-[#0263cc]/90 w-full sm:w-auto"
                >
                  {addingStopShiftId === currentShift._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {addingStopShiftId === currentShift._id ? "Adding..." : "Add Stop"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              {currentShift.stops.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No stops added yet</p>
                  <p className="text-sm">Add your first stop to get started</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={(result) => onDragEnd(result, currentShift._id)}>
                  <Droppable droppableId={currentShift._id}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                        {currentShift.stops.map((stop, idx) => (
                          <Draggable key={stop._id} draggableId={stop._id} index={idx}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`transition-all ${
                                  snapshot.isDragging
                                    ? "shadow-lg ring-2 ring-[#0263cc]/20 bg-[#0263cc]/5"
                                    : "hover:shadow-md"
                                }`}
                              >
                                <CardContent className="p-3 sm:p-4">
                                  <div className="flex items-start gap-2 sm:gap-4">
                                    <div
                                      {...provided.dragHandleProps}
                                      className="mt-2 p-1 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing shrink-0"
                                    >
                                      <GripVertical className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                    </div>

                                    <div className="flex-1 min-w-0 space-y-3 sm:space-y-4">
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                                        {/* Stop Name */}
                                        <div className="space-y-2 md:col-span-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <Label htmlFor={`stop-name-${stop._id}`}>Stop Name</Label>
                                            {stop.isCheckpoint && (
                                              <Badge variant="secondary" className="gap-1 bg-orange-100 text-orange-700 border-orange-200">
                                                <Flag className="h-3 w-3" />
                                                Checkpoint
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="text-sm font-semibold text-[#0263cc] mb-1">Stop #{idx + 1}::</div>
                                          <Input
                                            id={`stop-name-${stop._id}`}
                                            value={stop.name}
                                            onChange={(e) => handleEditStop(currentShift._id, stop._id, "name", e.target.value)}
                                            placeholder="Enter stop name"
                                            className="focus:ring-[#0263cc] focus:border-[#0263cc]"
                                          />
                                        </div>

                                        {/* Latitude */}
                                        <div className="space-y-2 md:col-span-1">
                                          <Label htmlFor={`latitude-${stop._id}`}>Latitude</Label>
                                          <div className="flex flex-wrap gap-2 items-center">
                                            <Input
                                              id={`latitude-${stop._id}`}
                                              value={stop.latitude}
                                              onChange={(e) => handleEditStop(currentShift._id, stop._id, "latitude", e.target.value)}
                                              placeholder="28.6139"
                                              className="focus:ring-[#0263cc] focus:border-[#0263cc] min-w-0 flex-1"
                                            />
                                            <div className="flex gap-2">
                                              <Button
                                                type="button"
                                                size="sm"
                                                className="bg-green-500 text-white px-2 py-1 shrink-0"
                                                onClick={() => handlePasteCoordinates(currentShift._id, stop._id)}
                                                title="Paste coordinates from clipboard (format: lat, lng)"
                                              >
                                                <ClipboardPaste className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                type="button"
                                                size="sm"
                                                className="bg-blue-500 text-white px-2 py-1 shrink-0 text-xs sm:text-sm"
                                                onClick={() => {
                                                  const lat = parseFloat(stop.latitude)
                                                  const lng = parseFloat(stop.longitude)
                                                  let mapsUrl = 'https://www.google.com/maps'
                                                  if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && (lat !== 0 || lng !== 0)) {
                                                    mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
                                                  }
                                                  window.open(mapsUrl, '_blank')
                                                  toast({
                                                    title: "Instructions",
                                                    description: "Google Maps opened in new tab. Search for location, copy coordinates (from URL or right-click), then click the paste button to auto-fill both fields.",
                                                    duration: 5000
                                                  })
                                                }}
                                                title="Open Google Maps to search location and copy coordinates"
                                              >
                                                Pick on Map
                                              </Button>
                                              <Button
                                                type="button"
                                                size="sm"
                                                className="bg-violet-500 text-white px-2 py-1 shrink-0 text-xs sm:text-sm"
                                                onClick={() => handleSetCurrentLocation(currentShift._id, stop._id)}
                                                disabled={currentLocationStopId === stop._id}
                                                title="Use your current GPS location for this stop"
                                              >
                                                {currentLocationStopId === stop._id ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <>
                                                    <Locate className="h-4 w-4 sm:mr-1" />
                                                    <span className="hidden sm:inline">Current Location</span>
                                                  </>
                                                )}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Longitude */}
                                        <div className="space-y-2 md:col-span-1">
                                          <Label htmlFor={`longitude-${stop._id}`}>Longitude</Label>
                                          <Input
                                            id={`longitude-${stop._id}`}
                                            value={stop.longitude}
                                            onChange={(e) => handleEditStop(currentShift._id, stop._id, "longitude", e.target.value)}
                                            placeholder="77.2090"
                                            className="focus:ring-[#0263cc] focus:border-[#0263cc]"
                                          />
                                        </div>
                                      </div>

                                      {/* Checkpoint toggle */}
                                      <div className="flex items-center space-x-2 pt-2">
                                        <input
                                          type="checkbox"
                                          id={`checkpoint-${stop._id}`}
                                          checked={stop.isCheckpoint || false}
                                          onChange={(e) => handleEditStop(currentShift._id, stop._id, "isCheckpoint", e.target.checked)}
                                          className="h-4 w-4 rounded border-gray-300 text-[#0263cc] focus:ring-[#0263cc]"
                                        />
                                        <Label htmlFor={`checkpoint-${stop._id}`} className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                                          <Flag className="h-4 w-4 text-[#0263cc] shrink-0" />
                                          <span className="truncate">Mark as Checkpoint</span>
                                        </Label>
                                      </div>

                                      {/* Actions */}
                                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-1">
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleOpenStudentModal(stop, currentShift)}
                                            className="gap-2 border-[#0263cc] text-[#0263cc] hover:bg-[#0263cc] hover:text-white w-full sm:w-auto"
                                          >
                                            <Users className="h-4 w-4 shrink-0" />
                                            Manage Students
                                          </Button>
                                          {stop.students && stop.students.length > 0 && (
                                            <div className="flex items-center gap-2">
                                              <Badge variant="secondary" className="gap-1 bg-[#0263cc]/10 text-[#0263cc]">
                                                <Users className="h-3 w-3" />
                                                {stop.students.length}
                                              </Badge>
                                              <span className="text-xs sm:text-sm text-gray-600">
                                                {stop.students.length} student(s) assigned
                                              </span>
                                            </div>
                                          )}
                                        </div>

                                        <div className="flex items-center gap-2 flex-wrap">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleCopyStop(currentShift._id, stop)}
                                            className="gap-2"
                                          >
                                            <Copy className="h-4 w-4" />
                                            Copy
                                          </Button>
                                          <Button variant="outline" size="sm" className="gap-2 bg-transparent">
                                            <Edit3 className="h-4 w-4" />
                                            Edit
                                          </Button>
                                          <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                              <Button variant="outline" size="sm" className="gap-2 text-red-500 hover:text-red-700 bg-transparent">
                                                <Trash2 className="h-4 w-4" />
                                                Delete
                                              </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent>
                                              <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Stop</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                  Are you sure you want to delete "{stop.name}"? This action cannot be undone.
                                                </AlertDialogDescription>
                                              </AlertDialogHeader>
                                              <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                  onClick={() => handleDeleteStop(currentShift._id, stop._id)}
                                                  disabled={deletingStopId === stop._id}
                                                  className="bg-red-600 hover:bg-red-700 disabled:opacity-70"
                                                >
                                                  <span className="inline-flex items-center gap-2">
                                                    {deletingStopId === stop._id && <Loader2 className="h-4 w-4 animate-spin" />}
                                                    {deletingStopId === stop._id ? "Deleting..." : "Delete"}
                                                  </span>
                                                </AlertDialogAction>
                                              </AlertDialogFooter>
                                            </AlertDialogContent>
                                          </AlertDialog>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </CardContent>
          </Card>
        )}

        {/* Student Assignment Modal */}
        <StudentAssignmentModal
          isOpen={studentModalOpen}
          onClose={() => setStudentModalOpen(false)}
          stop={selectedStop}
          shiftId={currentShift?._id}
          routeId={vehicleRoute?._id}
          onStudentsUpdated={(studentIds) => handleStudentsUpdated(studentIds, currentShift)}
          availableStudents={availableStudents}
        />

        {/* Shift Type Selection Modal */}
        <Dialog open={shiftTypeModalOpen} onOpenChange={(open) => {
          setShiftTypeModalOpen(open)
          if (!open) setNewShiftType('pickup')
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Select Shift Type</DialogTitle>
              <DialogDescription>Choose whether this shift is for pickup or drop</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Shift Type</Label>
                <Select value={newShiftType} onValueChange={setNewShiftType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="drop">Drop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShiftTypeModalOpen(false); setNewShiftType('pickup') }}>
                Cancel
              </Button>
              <Button onClick={handleCreateShift} className="bg-[#0263cc] hover:bg-[#0263cc]/90">
                Create Shift
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Shift Confirmation Modal */}
        <Dialog open={deleteShiftModalOpen} onOpenChange={setDeleteShiftModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Shift</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{shiftToDelete?.name}"? This action will permanently delete the shift and all its stops. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteShiftModalOpen(false)}>Cancel</Button>
              <Button
                onClick={handleDeleteShiftConfirmed}
                disabled={deletingShiftId === shiftToDelete?._id}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-70"
              >
                <span className="inline-flex items-center gap-2">
                  {deletingShiftId === shiftToDelete?._id && <Loader2 className="h-4 w-4 animate-spin" />}
                  {deletingShiftId === shiftToDelete?._id ? "Deleting..." : "Delete Shift"}
                </span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Shift Confirmation Modal */}
        <Dialog open={copyShiftModalOpen} onOpenChange={setCopyShiftModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Copy Stops</DialogTitle>
              <DialogDescription>
                {shiftToCopy && (
                  <>
                    Copy stops from <strong>"{shiftToCopy.name}"</strong> ({shiftToCopy.type}) to{" "}
                    <strong>{shiftToCopy.type === 'pickup' ? 'Drop' : 'Pickup'}</strong> shift?
                    <br /><br />
                    This will {vehicleRoute.shifts.some(s => s.type === (shiftToCopy.type === 'pickup' ? 'drop' : 'pickup'))
                      ? 'replace all existing stops in the target shift'
                      : 'create a new shift'} with stops in reversed order. Students will not be copied.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCopyShiftModalOpen(false) }}>
                Cancel
              </Button>
              <Button onClick={handleCopyShiftConfirmed} className="bg-[#0263cc] hover:bg-[#0263cc]/90">
                Copy Stops
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  )
}
